import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { Link } from "react-router-dom";
import { TableData } from "../../../core/data/interface";
import Table from "../../../core/common/dataTable/index";
import EmailTemplateModal from "../../../core/modals/email_template_modal";
import { Pagination } from "antd";
import { getEmailTemplates } from "./emailTemplateAPI";

export type EmailTemplateType = {
  id: number;
  uuid: string;
  name: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  Action: string;
  key: string;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

type EmailTemplateFilter = {
  pageNo: number;
  pageSize: number;
  name?: string;
  startDate?: Date;
  endDate?: Date;
};

const defaultEmailTemplateFilter = {
  pageNo: 0,
  pageSize: 5,
  name: "",
  type: undefined,
  startDate: undefined,
  endDate: undefined,
  status: undefined,
};

const EmailTemplate = () => {
  const [filter, setFilter] = useState<EmailTemplateFilter>(
    defaultEmailTemplateFilter
  );

  const [totalElements, setTotalElements] = useState<number>(0);

  const [data, setData] = useState<EmailTemplateType[]>([]);

  const [recordId, setRecordId] = useState<number>();

  const [action, setAction] = useState<"edit" | "delete">();

  const [reload, setReload] = useState<boolean>(false);

  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      if (filter.name !== searchValue) {
        // Only update if the value actually changed
        setFilter((prevFilter) => ({ ...prevFilter, name: searchValue }));
      }
    }, 500); // Delay of 500ms

    return () => clearTimeout(handler); // Cleanup on every keystroke
  }, [searchValue, filter.name]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  useEffect(() => {
    const fetchEmailTemplateData = async () => {
      try {
        const { data } = await getEmailTemplates(filter);

        const cData = data?.data?.content;

        const emailTemplateData = cData.map(
          (d: EmailTemplateType, index: number) => ({
            ...d,
            Action: "Edit      Delete",
            key: `${index}`,
          })
        );
        console.log(emailTemplateData);
        setData(emailTemplateData);
        setTotalElements(data?.data?.totalElements);
      } catch (error) {
        console.error("Error fetching emailTemplates:", error);
      }
    };

    fetchEmailTemplateData();
  }, [reload, filter]);

  const handleEditEmailTemplate = (id: number) => {
    setRecordId(id);
    setAction("edit");
  };

  const handleDeleteEmailTemplate = (id: number) => {
    setRecordId(id);
    setAction("delete");
  };

  const [stars, setStars] = useState<{ [key: number]: boolean }>({});

  React.useEffect(() => {
    const initializeStarsState = () => {
      const starsState: { [key: number]: boolean } = {};
      data.forEach((item, index) => {
        starsState[index] = false;
      });
      setStars(starsState);
    };

    initializeStarsState();
  }, [data]);

  const handleStarToggle = (index: number) => {
    setStars((prevStars) => ({
      ...prevStars,
      [index]: !prevStars[index],
    }));
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      width: 300,
      sorter: (a: TableData, b: TableData) => a.name.localeCompare(b.name),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      sorter: (a: TableData, b: TableData) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (text: string) => <span>{formatDate(text)}</span>,
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      sorter: (a: TableData, b: TableData) => a.createdBy.localeCompare(b.createdBy),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      sorter: (a: TableData, b: TableData) =>
        new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      render: (text: string) => <span>{formatDate(text)}</span>,
    },
    {
      title: "Updated By",
      dataIndex: "updatedBy",
      sorter: (a: TableData, b: TableData) => a.updatedBy.localeCompare(b.updatedBy),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      width: 50,
      render: (text: string, record: any, index: number) => (
        <div className="dropdown table-action">
          <Link
            to="#"
            className="action-icon"
            data-bs-toggle="dropdown"
            aria-expanded="true"
          >
            <i className="fa fa-ellipsis-v"></i>
          </Link>
          <div
            className="dropdown-menu dropdown-menu-right"
            style={{
              position: "absolute",
              inset: "0px auto auto 0px",
              margin: "0px",
              transform: "translate3d(-99.3333px, 35.3333px, 0px)",
            }}
            data-popper-placement="bottom-start"
          >
            <Link
              onClick={() => handleEditEmailTemplate(record?.id)}
              className="dropdown-item"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvas_edit"
              to="#"
            >
              <i className="ti ti-edit text-blue"></i> Edit
            </Link>
            <Link
              onClick={() => handleDeleteEmailTemplate(record?.id)}
              className="dropdown-item"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#delete_emailTemplate"
            >
              <i className="ti ti-trash text-danger"></i> Delete
            </Link>
          </div>
        </div>
      ),
    },
  ];
  const initialSettings = {
    endDate: new Date(),
    ranges: {
      "Last 30 Days": [
        dayjs().subtract(30, "days").startOf("day").toDate(),
        new Date(),
      ],
      "Last 7 Days": [
        dayjs().subtract(7, "days").startOf("day").toDate(),
        new Date(),
      ],
      "Last Month": [
        dayjs().subtract(1, "month").startOf("month").startOf("day").toDate(),
        dayjs().subtract(1, "month").endOf("month").endOf("day").toDate(),
      ],
      "This Month": [
        dayjs().startOf("month").startOf("day").toDate(),
        dayjs().endOf("month").endOf("day").toDate(),
      ],
      Today: [dayjs().startOf("day").toDate(), dayjs().endOf("day").toDate()],
      Yesterday: [
        dayjs().subtract(1, "day").startOf("day").toDate(),
        dayjs().subtract(1, "day").endOf("day").toDate(),
      ],
    },
    startDate: dayjs().subtract(7, "days").startOf("day").toDate(), // Set "Last 7 Days" as default
    timePicker: false,
  };

  const handleDateRangePickerCallback = (start: Date, end: Date) => {
    setFilter({
      ...filter,
      startDate: dayjs(start).toDate(),
      endDate: dayjs(end).toDate(),
    });
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">Email Template</h4>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
              {/* EmailTemplate List */}
              <div className="card">
                <div className="card-header">
                  {/* Search */}
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="icon-form mb-3 mb-sm-0">
                        <span className="form-icon">
                          <i className="ti ti-search" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search Email Template"
                          name="search"
                          value={searchValue}
                          onChange={handleSearch}
                        />
                      </div>
                    </div>
                    <div className="col-sm-4">
                      <div>
                        <div className="d-flex align-items-center flex-wrap row-gap-2">
                          <div className="icon-form">
                            <span className="form-icon">
                              <i className="ti ti-calendar" />
                            </span>
                            <DateRangePicker
                              initialSettings={initialSettings}
                              onCallback={handleDateRangePickerCallback}
                            >
                              <input
                                className="form-control bookingrange"
                                type="text"
                              />
                            </DateRangePicker>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-4">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_add"
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Add Email Template
                        </Link>
                      </div>
                    </div>
                  </div>
                  {/* /Search */}
                </div>

                <div className="card-body">
                  {/* EmailTemplate List */}
                  <div className="col-sm-12 table-responsive">
                    <Table dataSource={data} columns={columns} />
                  </div>

                  <div className="row align-items-center mt-4">
                    <div className="col-md-12">
                      <Pagination
                        current={filter.pageNo + 1} // Current page number
                        total={totalElements} // Total number of items
                        pageSize={filter.pageSize} // Items per page
                        onChange={(page) =>
                          setFilter({ ...filter, pageNo: page - 1 })
                        } // Callback function
                      />
                    </div>
                  </div>
                  {/* /EmailTemplate List */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      <EmailTemplateModal
        action={action}
        recordId={recordId}
        setRecordId={setRecordId}
        reload={reload}
        setReload={setReload}
      />
    </>
  );
};

export default EmailTemplate;
